// @flow
import * as React from 'react';

import debounce from 'lodash/debounce';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectCartUUID,
  selectCartReselleeID,
  setCartReselleeId,
  setCartProductOwner,
  setCartOwnerIsReseller,
} from '@gandi/cart';
import { CountryName } from '@gandi/react-places';
import { selectCurrentId, type Organization } from '@gandi/redux-sharing';
import { type TranslatorProps, useTranslator } from '@gandi/translate';
import { Base, Text } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';

import { selectShopCurrent } from '../../../modules/organization/selectors';
import type {
  PreFetchArgs,
  FullState,
  ComponentWithPrefetch,
} from '../../../types';
import {
  LIST_API_LIMIT,
  fetchCustomersList,
  fetchOneCustomer,
  selectCustomersList,
  selectCustomersCount,
  selectIsLoadedCustomers,
} from '../../Customer/modules/customers';

import type { CustomerListState as CustomersListType } from '../../Customer/types';

import styles from './PayerSelect.scss';

type Customer = CustomersListType[0];

type OwnProps = {
  frozen: boolean,
};

type StateProps = {
  customers: Array<Customer>,
  totalCount: number | void,
  cartCustomer: string,
  reseller: Organization,
  isSamlUser: boolean,
  sharingId: string,
  cartId: string,
  // eslint-disable-next-line react/no-unused-prop-types
  key: string,
};

type Props = {
  ...OwnProps,
  ...StateProps,
};

const textWrapStyle = { whiteSpace: 'normal' };

function stringify({ data }: any): string {
  return Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');
}

const filterOpt = createFilter({ stringify });

function getCustomerEdit(
  customer: Customer | Organization,
  current: string,
  cart: string,
) {
  const base = `/cart/${cart}/configure`;

  if (customer.id === current) {
    return `${base}/account/organization`;
  }

  return `${base}/customer/${encodeURIComponent(customer.name)}/${
    customer.id
  }/edit`;
}

function getId(org: Customer): string {
  return org.id || '';
}

function getTypeLabel(__: TranslatorProps['__'], type: Customer['type']) {
  if (type === 'individual') return __('Individual');
  if (type === 'company') return __('Company');
  if (type === 'association') return __('Association');
  if (type === 'publicbody') return __('Public Body');
  return __('Organization');
}

function getDisplayName(org: Customer | Organization) {
  const legal = org.type !== 'individual' ? org.orgname || org.name : undefined;
  // $FlowFixMe[incompatible-type]
  return legal || (org.firstname ? `${org.firstname} ${org.lastname}` : '');
}

function getLegalName(org: Customer | Organization): string {
  // $FlowFixMe[incompatible-type]
  return org.firstname ? `${org.firstname} ${org.lastname}` : '';
}

export function renderOption(
  __: TranslatorProps['__'],
  org: Partial<Customer> | Organization,
): React.Node {
  return (
    // $FlowFixMe[incompatible-type]
    <Base padding="small" key={`customer-${org.id}`}>
      <Base tagName="p" margin="none">
        <Base flex flexAlignItemsCenter tagName="span">
          <Text size="big" weight="semibold">
            {/* $FlowFixMe[incompatible-call] */}
            {getDisplayName(org)}
          </Text>
        </Base>
        <Text color="light" size="small" transform="uppercase">
          {
            // $FlowFixMe[incompatible-call]
            getTypeLabel(__, org.type)
          }
        </Text>
        {org.type !== 'individual' && (
          <>
            <br />
            {/* $FlowFixMe[incompatible-call] */}
            <span>{getLegalName(org)}</span>
          </>
        )}
        {org.email && (
          <>
            <br />
            <span>{org.email}</span>
          </>
        )}
        {org.country && org.city && org.streetaddr && (
          <>
            <br />
            <span style={textWrapStyle}>
              {`${org.streetaddr}${org.zip ? `, ${org.zip}` : ''}, ${
                org.city
              }, `}
              <CountryName country={org.country} />
            </span>
          </>
        )}
      </Base>
    </Base>
  );
}

function onChange(
  callback: (id: string) => void,
  current: void | Customer,
  update: (next: void | Customer) => any,
  next_: any,
) {
  const next = Array.isArray(next_) ? next_[0] : next_;
  if (next !== current) {
    update(next);
    if (next && typeof callback === 'function') {
      callback(next.id);
    }
  }
}

function onSearch(
  callback: string => mixed,
  count: number | void,
  value: string = '',
) {
  // $FlowFixMe[sketchy-number-and]
  if ((count && count > LIST_API_LIMIT) || !value) {
    callback(value);
  }
}

function CustomerSelect({
  customers,
  cartCustomer,
  reseller,
  isSamlUser,
  sharingId,
  cartId,
  frozen,
  totalCount,
}: Props): React.MixedElement {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __ } = translator;
  /* --------------- Force a re-render after hydration/mount. --------------- */
  // Required to mitigate a SSR/hydration bug in Safari
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, setMounted]);
  /* ------------------------------------------------------------------------ */

  /* ----------- Reset possibly conflicting cart ownership state. ----------- */
  // Required to prevent possible desync cases when user selects a product owner
  // for the cart, then change their mind a select a customer.
  React.useEffect(() => {
    dispatch(setCartProductOwner(null));
  }, [dispatch]);
  /* ------------------------------------------------------------------------ */

  const [customer, set] = React.useState(
    customers.find(candidate => candidate.id === cartCustomer),
  );

  const candidates = React.useMemo(
    () =>
      [
        customer,
        ...[...customers]
          .filter(candidate => candidate.id !== customer?.id)
          .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
      ].filter(Boolean),
    [customer, customers],
  );

  const [searchAPI, setSearchAPI] = React.useState('');

  const checkPresetCustomer =
    !searchAPI && cartCustomer && cartCustomer !== customer?.id;

  // In the case where a customerId has been preset in the cart state
  // after customer creation, check if that customer is present in the
  // first page of customers, if not, fetch it and save it in the state.
  React.useEffect(() => {
    async function doFetch() {
      const { data: list } = await dispatch(
        fetchCustomersList(sharingId, searchAPI),
      );

      const fetchPresetCustomer =
        checkPresetCustomer && !list.find(({ id }) => id === cartCustomer);

      if (fetchPresetCustomer) {
        const { data: one } = await dispatch(
          fetchOneCustomer(sharingId, cartCustomer),
        );
        set(one);
        dispatch(setCartOwnerIsReseller(null));
      }
    }

    doFetch();
    // We only need to run this effect on mount + whenever `searchAPI` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAPI]);

  const afterChange = React.useCallback(
    (id: string) => {
      dispatch(setCartReselleeId(id));
      dispatch(setCartOwnerIsReseller(null));
      if (searchAPI) {
        setSearchAPI('');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCartReselleeId, searchAPI, setSearchAPI],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = React.useCallback(
    onChange.bind(null, afterChange, customer, set),
    [afterChange, customer, set],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = React.useCallback(
    debounce(onSearch.bind(null, setSearchAPI, totalCount), 500),
    [setCartReselleeId, setSearchAPI, totalCount],
  );

  // Update the React state with a newly created or updated customer
  // from the Redux state.
  React.useEffect(() => {
    if (cartCustomer) {
      const updated = candidates.find(
        candidate => candidate.id === cartCustomer,
      );
      if (updated !== customer) {
        set(updated);
      }
    }
  }, [cartCustomer, candidates, customer, set]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderOpt = React.useCallback(renderOption.bind(null, __), [__]);

  const renderNoOpt = React.useCallback(
    () => __('No customers found matching your search.'),
    [__],
  );

  // Re-mount the component when going from:
  // - default value exists but is not yet loaded
  // - default value exists and is now loaded
  // Occurs when creating a new customer and wanting it auto-selected.
  // - switching reseller
  const key =
    cartCustomer && !customer
      ? `${reseller.id}-not-loaded`
      : `${reseller.id}-loaded`;

  return (
    <Base className="wrapper-sm" flex flexCol>
      <div className={styles.container}>
        <Custom
          key={key}
          id="customer-select-container"
          inputId="customer-select-input"
          instanceId="customer-select-instance"
          name="customer"
          translator={translator}
          isClearable={false}
          isDisabled={!mounted || frozen}
          defaultValue={customer}
          options={candidates}
          getOptionValue={getId}
          filterOption={filterOpt}
          renderOption={renderOpt}
          maxMenuHeight={500}
          noOptionsMessage={renderNoOpt}
          onChange={handleChange}
          onInputChange={handleSearch}
        />
      </div>
      <span className={styles.linksContainer}>
        {customer && (
          <>
            <Link to={getCustomerEdit(customer, sharingId, cartId)}>
              {translator.__('Edit %(name)s', {
                name: getDisplayName(customer),
              })}
            </Link>
            {!isSamlUser && <hr className={styles.separator} />}
          </>
        )}
        {!isSamlUser && (
          <Link to={`/cart/${cartId}/configure/customer/create`}>
            {__('Create New')}
          </Link>
        )}
      </span>
    </Base>
  );
}

CustomerSelect.preFetch = (
  args: PreFetchArgs<{}, void, empty, FullState>,
): Promise<any> => {
  const state = args.getState();
  const sharingId = selectCurrentId(state);
  const loadedCustomers = selectIsLoadedCustomers(state, sharingId);
  return !loadedCustomers
    ? args.dispatch(fetchCustomersList(sharingId))
    : Promise.resolve();
};

const mapStateToProps = (state: FullState): StateProps => {
  const sharingId = selectCurrentId(state);
  const reseller = selectShopCurrent(state, true);
  const cartCustomer = selectCartReselleeID(state);
  const customers = selectCustomersList(state);
  const totalCount = selectCustomersCount(state);
  const cartId = selectCartUUID(state);
  const key = reseller.id;
  const isSamlUser = selectIsSamlUser(state);

  return {
    customers,
    totalCount,
    cartCustomer,
    reseller,
    isSamlUser,
    sharingId,
    cartId,
    // Re-mount the whole component when switching resellers.
    key,
  };
};

// $FlowFixMe[underconstrained-implicit-instantiation]
export default (connect(mapStateToProps)(CustomerSelect): ComponentWithPrefetch<
  OwnProps,
  typeof CustomerSelect.preFetch,
>);
